/**
 * "Give a free month" — a card on a user's detail in the Admin Portal (free month for paying
 * subscribers, PR-2, 2026-09-30; docs/architecture/38-PARTNER-ATTRIBUTION.md §19).
 *
 * A button, then a dialog in the page (no window.confirm): pick a reason, read the preflight
 * (where the month goes — free access, an App Store extend, a Google Play defer, queued, or refused —
 * and the new end or next charge), then confirm. The server routes it by who pays
 * (services/free_month_gift.py). One dialog carries one idempotency key, so a double click never
 * gives two months.
 *
 * Hidden entirely when the server says FF_ADMIN_FREE_MONTH is off, and for a session that cannot
 * write (support / viewer — the server would answer 403 anyway). Web-only (the admin SPA); every
 * request goes through the shared `adminFetch` (web/src/utils/adminApi.ts).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdminApiError, adminFetch } from '../../utils/adminApi';
import { useAdminRole } from '../../pages/admin/partners/PartnersShared';
import {
  GIFT_REASONS,
  NOTES_MAX,
  canConfirm,
  configPath,
  describeError,
  describeResult,
  giveBody,
  givePath,
  keyIsSpent,
  nextKey,
  spentAfterError,
  preflightLines,
  preflightPath,
  showGiveButton,
  type FreeMonthConfig,
  type FreeMonthPreflight,
  type FreeMonthResult,
  type GiftReason,
} from './giveFreeMonthLogic';

interface Props {
  userId: string;
}

function errText(err: unknown): string {
  if (err instanceof AdminApiError) return describeError(err.status, err.body);
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export const GiveFreeMonthPanel: React.FC<Props> = ({ userId }) => {
  const { writable } = useAdminRole();
  const [config, setConfig] = useState<FreeMonthConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<GiftReason>('goodwill');
  const [notes, setNotes] = useState('');
  const [preflight, setPreflight] = useState<FreeMonthPreflight | null>(null);
  const [result, setResult] = useState<FreeMonthResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // One key per person until an answer arrives for it: reopening after a lost response re-sends the
  // SAME key, so the server answers from its ledger (never a second month). A second click in the
  // same tick finds `inFlight` set.
  const keyRef = useRef<string | null>(null);
  const spentRef = useRef(false);
  const inFlight = useRef(false);

  useEffect(() => {
    keyRef.current = null;          // a new person, a new key
    spentRef.current = false;
    let live = true;
    adminFetch<FreeMonthConfig>(configPath(userId))
      .then((c) => { if (live) setConfig(c); })
      .catch(() => { if (live) setConfig(null); });   // unknown → hidden (fail closed)
    return () => { live = false; };
  }, [userId]);

  const openDialog = useCallback(async () => {
    keyRef.current = nextKey(keyRef.current, spentRef.current);
    spentRef.current = false;
    setOpen(true);
    setResult(null);
    setError(null);
    setPreflight(null);
    setBusy(true);
    try {
      setPreflight(await adminFetch<FreeMonthPreflight>(preflightPath(userId)));
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }, [userId]);

  const give = useCallback(async () => {
    if (inFlight.current || !canConfirm(preflight, busy)) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const r = await adminFetch<FreeMonthResult>(givePath, {
        method: 'POST',
        body: JSON.stringify(giveBody(userId, reason, notes, keyRef.current || '')),
      });
      spentRef.current = keyIsSpent(r);
      setResult(r);
    } catch (e) {
      spentRef.current = spentAfterError(e instanceof AdminApiError ? e.status : null);
      setError(errText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [busy, notes, preflight, reason, userId]);

  if (!showGiveButton(config, writable)) return null;

  const shown = result ? describeResult(result) : null;
  const toneColor = shown?.tone === 'ok' ? 'var(--cedar-primary)' : shown?.tone === 'warn' ? '#92400e' : '#b91c1c';

  return (
    <div className="mt-3 p-3 rounded border" style={{ borderColor: 'var(--border)' }} data-testid="give-free-month">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium" style={{ color: 'var(--cedar-dark)' }}>Give a free month</div>
          <div className="text-xs text-gray-500">
            {config?.days ?? 31} days. A payer&apos;s next charge moves back; anyone else gets free Coach Cedar access.
          </div>
        </div>
        {!open && (
          <button
            onClick={openDialog}
            className="text-xs px-3 py-1.5 text-white rounded"
            style={{ backgroundColor: 'var(--cedar-primary)' }}
            data-testid="give-free-month-open"
          >
            Give a free month
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-2" role="dialog" aria-label="Give a free month">
          {!result && (
            <>
              <label className="block text-xs text-gray-600">
                Reason
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value as GiftReason)}
                  className="mt-1 block w-full text-xs px-2 py-1 rounded border"
                  disabled={busy}
                >
                  {GIFT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </label>
              <label className="block text-xs text-gray-600">
                Note (kept in the audit log)
                <textarea
                  value={notes}
                  maxLength={NOTES_MAX}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 block w-full text-xs px-2 py-1 rounded border"
                  rows={2}
                  disabled={busy}
                />
              </label>
              {busy && !preflight && <div className="text-xs text-gray-500">Checking who pays…</div>}
              {preflight && (
                <ul className="text-xs list-disc pl-4 space-y-0.5" data-testid="give-free-month-preflight">
                  {preflightLines(preflight).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={give}
                  disabled={!canConfirm(preflight, busy)}
                  className="text-xs px-3 py-1.5 text-white rounded disabled:opacity-50"
                  style={{ backgroundColor: 'var(--cedar-primary)' }}
                  data-testid="give-free-month-confirm"
                >
                  {busy && preflight ? 'Giving…' : 'Give it'}
                </button>
                <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 rounded border" disabled={busy}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {shown && (
            <div className="text-xs" data-testid="give-free-month-result">
              <div className="font-medium" style={{ color: toneColor }}>{shown.headline}</div>
              <div className="text-gray-600">{shown.detail}</div>
              <button onClick={() => setOpen(false)} className="mt-2 text-xs px-3 py-1.5 rounded border">Close</button>
            </div>
          )}
          {error && <div className="text-xs" style={{ color: '#b91c1c' }}>{error}</div>}
        </div>
      )}
    </div>
  );
};
